import { Dossier } from "@rumble-it/common-lib/types/models/Dossier.interface";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Divider,
  message,
  notification,
  Progress,
  Space,
  Tag,
  Typography,
  List,
} from "antd";
import { processDossierAsDealership } from "../../../../lib/api/resources/dossiers";
import { useState } from "react";
import { Button, Card, Checkbox } from "@rumbledirect/ui";

interface DossierActionsCardProps {
  dossier: Dossier | undefined;
  isLoading?: boolean;
}

interface processMutationParams {
  processType: "gate-in" | "notify-driver" | "gate-out";
  successMessage: string;
  successDescription?: string;
}

export type MissingItem = {
  key: string;
  label: string;
  description?: string | JSX.Element;
  required?: boolean;
};

const MOCK_MISSING_ITEMS: MissingItem[] = [
  {
    key: "damage_photos",
    label: "Schadefoto’s",
    description: (
      <div>
        De AI analyse van de schadefoto's heeft aangetoond dat de kwaliteit
        mogelijk onvoldoende is voor een correcte beoordeling.{" "}
        <a className="underline text-primary-500" href="#media">
          Bekijk hier de foto's
        </a>
        .
      </div>
    ),
    required: true,
  },
];

export const AgentDossierActionsCard = ({
  dossier,
  isLoading,
}: DossierActionsCardProps) => {
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  const getProgressFromId = (id: string): number => {
    if (id === "019a746e-bfe2-7fd6-86fe-c19cc4b6bb9b") return 50;

    let hash = 0;
    for (let i = 0; i < id.length; i++) {
      const char = id.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return Math.abs(hash) % 101; // Returns 0-100
  };

  const allKeys = MOCK_MISSING_ITEMS.map((item) => item.key);
  const allChecked =
    selectedKeys.length === allKeys.length && allKeys.length > 0;
  const indeterminate = selectedKeys.length > 0 && !allChecked;

  const handleToggleAll = (checked: boolean) => {
    setSelectedKeys(checked ? allKeys : []);
  };

  const handleToggleItem = (key: string, checked: boolean) => {
    setSelectedKeys((prev) =>
      checked ? [...prev, key] : prev.filter((k) => k !== key),
    );
  };

  const renderRequiredTag = (required?: boolean) => {
    let color: string = "default";
    if (required) color = "red";

    return <Tag color={color}>{required ? "Vereist" : "Optioneel"}</Tag>;
  };

  const queryClient = useQueryClient();
  const processMutation = useMutation({
    mutationFn: ({ processType }: processMutationParams) => {
      if (!dossier) {
        throw new Error("No dossier provided");
      }

      return processDossierAsDealership({
        dossierIri: dossier["@id"],
        processType,
      });
    },
    onSuccess: (_data, variables) => {
      notification.success({
        message: variables.successMessage,
        description: variables.successDescription,
      });
      queryClient.resetQueries();
    },
    onError: () => {
      message.error("Er is iets misgegaan.");
    },
  });

  return (
    <Card
      title="Dossier informatie"
      extra={
        MOCK_MISSING_ITEMS.length > 0 && (
          <Tag color="red">{MOCK_MISSING_ITEMS.length} ontbrekend item</Tag>
        )
      }
      loading={isLoading}
      className="w-full mb-5"
    >
      <div className="mb-5">
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Typography.Text type="secondary">Dossiervoortgang</Typography.Text>
          <Typography.Text type="secondary">
            {getProgressFromId(dossier?.id || "")}%
          </Typography.Text>
        </Space>
        <Progress
          percent={getProgressFromId(dossier?.id || "")}
          showInfo={false}
          size="small"
        />
      </div>
      <Checkbox
        indeterminate={indeterminate}
        checked={allChecked}
        onChange={(e) => handleToggleAll(e.target.checked)}
      >
        Alles selecteren
      </Checkbox>

      <List
        dataSource={MOCK_MISSING_ITEMS}
        split
        renderItem={(item) => {
          const checked = selectedKeys.includes(item.key);
          return (
            <List.Item actions={[renderRequiredTag(item.required)]}>
              <Space align="start">
                <Checkbox
                  checked={checked}
                  onChange={(e) => handleToggleItem(item.key, e.target.checked)}
                />
                <Space direction="vertical" size={0}>
                  <Typography.Text strong>{item.label}</Typography.Text>
                  {item.description && (
                    <Typography.Text type="secondary">
                      {item.description}
                    </Typography.Text>
                  )}
                </Space>
              </Space>
            </List.Item>
          );
        }}
      />

      <Divider style={{ margin: "8px 0" }} />
      <Space
        style={{ width: "100%", justifyContent: "space-between" }}
        align="center"
      >
        <Typography.Text type="secondary">
          {selectedKeys.length === 0
            ? "Geen items geselecteerd"
            : `${selectedKeys.length} item${
                selectedKeys.length > 1 ? "s" : ""
              } geselecteerd`}
        </Typography.Text>
        <Button
          type="primary"
          size="large"
          className="w-full mt-6"
          loading={processMutation.isPending}
          onClick={() =>
            processMutation.mutate({
              processType: "notify-driver",
              successMessage: "Herinnering verstuurd",
              successDescription:
                "De bestuurder is geïnformeerd over de ontbrekende items en gevraagd deze zo snel mogelijk aan te leveren.",
            })
          }
        >
          Herinnering versturen
        </Button>
      </Space>
    </Card>
  );
};
